import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AdvancedPDFViewer.css';

// PDF.js 类型定义
declare global {
  interface Window {
    pdfjsLib: any;
  }
}

interface AdvancedPDFViewerProps {
  documentId: string;
  filename: string;
  onClose: () => void;
  documentUrl?: string;
}

const AdvancedPDFViewer: React.FC<AdvancedPDFViewerProps> = ({
  documentId,
  filename,
  onClose,
  documentUrl
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfDocument, setPdfDocument] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 初始化PDF.js
  useEffect(() => {
    const initPDFJS = async () => {
      try {
        // 动态加载PDF.js
        if (!window.pdfjsLib) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.onload = () => {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            loadPDF();
          };
          document.head.appendChild(script);
        } else {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          loadPDF();
        }
      } catch (err) {
        setError('PDF.js 初始化失败');
        setLoading(false);
      }
    };

    initPDFJS();
  }, []);

  // 加载PDF文档
  const loadPDF = async () => {
    try {
      setLoading(true);
      setError(null);

      // 构建PDF URL
      const pdfUrl = documentUrl || `/api/documents/${documentId}/download`;
      console.log('正在加载PDF:', pdfUrl);

      // 先检查URL是否可访问并获取文件信息
      try {
        const response = await fetch(pdfUrl, { method: 'HEAD' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');
        
        console.log('PDF URL检查通过:', {
          status: response.status,
          contentType,
          contentLength
        });

        // 检查Content-Type
        if (contentType && !contentType.includes('application/pdf')) {
          throw new Error(`文件类型不是PDF: ${contentType}`);
        }

        // 检查文件大小
        if (contentLength && parseInt(contentLength) === 0) {
          throw new Error('PDF文件为空');
        }

      } catch (fetchError) {
        console.error('PDF URL检查失败:', fetchError);
        const errorMessage = fetchError instanceof Error ? fetchError.message : '未知错误';
        setError(`无法访问PDF文件: ${errorMessage}`);
        setLoading(false);
        return;
      }

      // 尝试直接获取PDF内容进行验证
      try {
        const response = await fetch(pdfUrl);
        const arrayBuffer = await response.arrayBuffer();
        
        // 检查文件头是否为PDF
        const uint8Array = new Uint8Array(arrayBuffer);
        const header = new TextDecoder().decode(uint8Array.slice(0, 8));
        
        if (!header.startsWith('%PDF')) {
          throw new Error('文件不是有效的PDF格式');
        }

        console.log('PDF文件验证通过，大小:', arrayBuffer.byteLength, '字节');

        // 使用ArrayBuffer加载PDF
        const loadingTask = window.pdfjsLib.getDocument({
          data: arrayBuffer,
          verbosity: 1 // 启用详细日志
        });

        const pdf = await loadingTask.promise;
        console.log('PDF加载成功，页数:', pdf.numPages);

        setPdfDocument(pdf);
        setTotalPages(pdf.numPages);
        setLoading(false);

      } catch (pdfError) {
        console.error('PDF内容验证或加载失败:', pdfError);
        throw pdfError;
      }

    } catch (err) {
      console.error('PDF加载失败:', err);
      let errorMessage = 'PDF文档加载失败';

      if (err instanceof Error) {
        if (err.name === 'InvalidPDFException') {
          errorMessage = 'PDF文件格式无效或已损坏，请检查文件是否完整';
        } else if (err.name === 'MissingPDFException') {
          errorMessage = 'PDF文件不存在或已被删除';
        } else if (err.name === 'UnexpectedResponseException') {
          errorMessage = '服务器响应异常，请检查网络连接';
        } else if (err.name === 'PasswordException') {
          errorMessage = 'PDF文件需要密码，请联系管理员';
        } else if (err.message) {
          errorMessage = `加载失败: ${err.message}`;
        }
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  // 渲染当前页面
  const renderPage = useCallback(async (pageNum: number) => {
    if (!pdfDocument || !canvasRef.current) return;

    try {
      const page = await pdfDocument.getPage(pageNum);
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      const viewport = page.getViewport({ scale, rotation });
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;
    } catch (err) {
      console.error('页面渲染失败:', err);
      setError('页面渲染失败');
    }
  }, [pdfDocument, scale, rotation]);

  // 页面变化时重新渲染
  useEffect(() => {
    if (pdfDocument && currentPage > 0 && currentPage <= totalPages) {
      renderPage(currentPage);
    }
  }, [pdfDocument, currentPage, scale, rotation, renderPage]);

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target !== document.body) return;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
          }
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
          }
          break;
        case 'Home':
          e.preventDefault();
          setCurrentPage(1);
          break;
        case 'End':
          e.preventDefault();
          setCurrentPage(totalPages);
          break;
        case 'Escape':
          if (fullscreen) {
            setFullscreen(false);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages, fullscreen]);

  if (loading) {
    return (
      <div className="advanced-pdf-viewer-overlay">
        <div className="advanced-pdf-viewer">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>正在加载PDF文档...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="advanced-pdf-viewer-overlay">
        <div className="advanced-pdf-viewer">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>加载失败</h3>
            <p>{error}</p>
            <button onClick={onClose} className="error-close-btn">
              关闭
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`advanced-pdf-viewer-overlay ${fullscreen ? 'fullscreen' : ''}`}>
      <div className="advanced-pdf-viewer">
        {/* 工具栏 */}
        <div className="toolbar">
          <div className="toolbar-left">
            <button onClick={onClose} className="toolbar-btn close-btn" title="关闭">
              ✕
            </button>
            <span className="filename">{filename}</span>
          </div>

          <div className="toolbar-center">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage <= 1}
              className="toolbar-btn"
              title="上一页 (←)"
            >
              ‹
            </button>

            <span className="page-info">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= totalPages) {
                    setCurrentPage(page);
                  }
                }}
                min={1}
                max={totalPages}
                className="page-input"
              />
              / {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
              className="toolbar-btn"
              title="下一页 (→)"
            >
              ›
            </button>
          </div>

          <div className="toolbar-right">
            {/* 缩放控制 */}
            <div className="zoom-controls">
              <button
                onClick={() => setScale(prev => Math.max(0.25, prev - 0.25))}
                className="toolbar-btn"
                title="缩小 (-)"
              >
                −
              </button>
              <span className="zoom-level">{Math.round(scale * 100)}%</span>
              <button
                onClick={() => setScale(prev => Math.min(5, prev + 0.25))}
                className="toolbar-btn"
                title="放大 (+)"
              >
                +
              </button>
            </div>

            {/* 旋转控制 */}
            <button
              onClick={() => setRotation(prev => (prev + 90) % 360)}
              className="toolbar-btn"
              title="旋转 (R)"
            >
              🔄
            </button>

            {/* 全屏 */}
            <button
              onClick={() => setFullscreen(!fullscreen)}
              className="toolbar-btn"
              title="全屏 (F11)"
            >
              {fullscreen ? '⛶' : '⛶'}
            </button>
          </div>
        </div>

        {/* 主要内容区域 */}
        <div className="content-area" ref={containerRef}>
          {/* PDF显示区域 */}
          <div className="pdf-container">
            <canvas ref={canvasRef} className="pdf-canvas" />
          </div>
        </div>

        {/* 状态栏 */}
        <div className="status-bar">
          <span>页面 {currentPage} / {totalPages}</span>
          <span>缩放: {Math.round(scale * 100)}%</span>
          {rotation !== 0 && <span>旋转: {rotation}°</span>}
        </div>
      </div>
    </div>
  );
};

export default AdvancedPDFViewer;
